// ============================================
// FILE: hooks/useAuth.ts (FIXED - Using Cookies)
// ============================================
import { useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { 
  login as loginService, 
  logout as logoutService, 
  logoutAllDevices as logoutAllDevicesService 
} from '../stateManagers/services/authService';
import { useAuthStore } from '../stateManagers/store/authStore';

export const useAuth = () => {
  const { 
    isAuthenticated, 
    user, 
    accessToken, 
    refreshToken, 
    loading, 
    setAuth, 
    clearAuth, 
    initAuth 
  } = useAuthStore();

  useEffect(() => {
    initAuth();
  }, [initAuth]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      console.log('🔐 useAuth: Starting login process...');
      const response = await loginService(email, password);
      
      console.log('📦 useAuth: Full response:', response.data);
      
      // Your authService already handles saving tokens to cookies
      // Just need to verify they were saved
      const savedAccessToken = Cookies.get('accessToken');
      const savedRefreshToken = Cookies.get('refreshToken');
      
      console.log('✅ useAuth: Verified saved accessToken:', savedAccessToken ? 'EXISTS' : 'MISSING');
      console.log('✅ useAuth: Verified saved refreshToken:', savedRefreshToken ? 'EXISTS' : 'MISSING');

      if (!savedAccessToken || !savedRefreshToken) {
        console.error('❌ useAuth: Tokens not saved to cookies!');
        throw new Error('Failed to save authentication tokens');
      }

      console.log('🔄 useAuth: Initializing auth from cookies...');
      initAuth(); // This will read from cookies and update the store
      
      console.log('✅ useAuth: Login complete!');
      return response;
    } catch (error: any) {
      console.error('❌ useAuth: Login error:', error);
      console.error('❌ useAuth: Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      throw new Error(errorMessage);
    }
  }, [initAuth]);

  const logout = useCallback(async () => {
    try {
      console.log('👋 useAuth: Starting logout...');
      if (accessToken && refreshToken) {
        await logoutService(accessToken, refreshToken);
      }
    } catch (error) {
      console.error('❌ useAuth: Logout error:', error);
    } finally {
      console.log('🧹 useAuth: Clearing auth data...');
      clearAuth();
    }
  }, [accessToken, refreshToken, clearAuth]);

  const logoutAll = useCallback(async () => {
    try {
      console.log('👋 useAuth: Logging out all devices...');
      if (accessToken) {
        await logoutAllDevicesService(accessToken);
      }
    } catch (error) {
      console.error('❌ useAuth: Logout all error:', error);
    } finally {
      console.log('🧹 useAuth: Clearing auth data...');
      clearAuth();
    }
  }, [accessToken, clearAuth]);

  return {
    isAuthenticated,
    user,
    loading,
    login,
    logout,
    logoutAll
  };
};